/**
 * MOCK KANBAN — bacheche per la modalità mock.
 *
 * Una board per pipeline (pipeline 1 "Vendite dirette", pipeline 2 "Post-vendita"). TS-101:
 * nessuna membership — ogni board mostra i preventivi con `currentStateId` tra gli stati della
 * propria pipeline. Per un mock credibile OGNI preventivo appartiene a una pipeline: si
 * distribuiscono in round-robin PIATTO su tutti gli stati di entrambe le pipeline (in ordine di
 * pipeline e posizione), così ogni colonna di entrambe le board risulta popolata. Stessa
 * distribuzione del seed SQL (db-seed-mocks-core.sql, crm_Quotation.currentStateId).
 */

import type { Kanban } from '../../entities/kanban/kanbanMappers';
import { mockQuotations } from './quotationMocks';
import { mockPipelines } from './pipelineMocks';
import { MOCK_GROUP_ID } from './mockSite';

const audit = {
  companyId: 1,
  groupId: MOCK_GROUP_ID,
  userId: 1,
  userName: 'Jader Francia',
  createDate: '2026-03-01T10:00:00Z',
  modifiedDate: '2026-03-08T10:00:00Z',
};

export const mockKanbans: Kanban[] = [
  {
    kanbanId: 5001,
    name: 'Trattative Q3',
    className: 'it.dvel.crm.core.model.Quotation',
    pipelineId: 1,
    ...audit,
  },
  {
    kanbanId: 5002,
    name: 'Assistenza Q3',
    className: 'it.dvel.crm.core.model.Quotation',
    pipelineId: 2,
    ...audit,
  },
  // Board di un tipo che il client non sa disegnare: si elenca, si apre, e la pagina spiega perche'
  // non mostra colonne. Vedi il commento sulla pipeline 3 in pipelineMocks.
  {
    kanbanId: 5003,
    name: 'Attività in lavorazione',
    className: 'it.dvel.crm.core.model.Task',
    pipelineId: 3,
    ...audit,
  },
];

// Round-robin PIATTO su tutti gli stati di tutte le pipeline (ordinate per id, stati per
// posizione): ogni preventivo cade su una pipeline e ogni colonna di ogni board è popolata.
// Deterministico per indice, così combacia con il seed SQL riga per riga.
// TS-117: SOLO i funnel del preventivo. Senza il filtro, dei preventivi finirebbero negli stati di
// un funnel di attivita' - cioe' il mock riprodurrebbe in piccolo esattamente il difetto che
// l'issue corregge nel backend: record di un tipo dentro stati di un altro.
const allStates = mockPipelines
  .slice()
  .filter((p) => p.className === 'it.dvel.crm.core.model.Quotation')
  .sort((a, b) => a.pipelineId - b.pipelineId)
  .flatMap((p) => [...p.states].sort((a, b) => a.position - b.position));
mockQuotations.forEach((quotation, i) => {
  if (allStates.length > 0)
    quotation.currentStateId = allStates[i % allStates.length].pipelineStateId;
});

/**
 * Una board → la board stessa e gli id degli stati della sua pipeline (TS-117).
 *
 * È il passo che ogni tipo kanbanizzabile ripete uguale prima di filtrare i propri record, e con
 * quattro tipi sarebbero state quattro copie. Il chiamante ci mette solo il confronto sul proprio
 * `currentStateId` e il proprio valutatore di filtro — la stessa divisione del backend, dove
 * `PipelineQuerySupport` è condiviso e cambia solo lo `scopeResolver`.
 *
 * Board inesistente → insieme di stati **vuoto**. Board di un ALTRO tipo → gli stati della sua
 * pipeline, in cui nessun record di questo tipo è collocato. In tutti e due i casi l'elenco è vuoto
 * e non contiene tutti i record: è la stessa risposta del backend, ed è ciò che distingue «filtra
 * per stati» da «filtra per tipo». Chi chiama non conti su un insieme vuoto per riconoscere il tipo
 * sbagliato: l'esclusione la fa il confronto sul proprio `currentStateId`.
 *
 * Sta qui e non in `mockHandlers.ts` perché serve anche ai plugin che mettono in bacheca un tipo
 * proprio (la Pratica di Pollini): `@crm/core` la esporta come `mockKanbanStateIds`, e da qui
 * esportarla non si porta dietro gli handler MSW.
 */
export function kanbanStateIds(kanbanId: number) {
  const kanban = mockKanbans.find((k) => k.kanbanId === kanbanId);
  const pipeline = mockPipelines.find((p) => p.pipelineId === kanban?.pipelineId);

  return { kanban, stateIds: new Set((pipeline?.states ?? []).map((s) => s.pipelineStateId)) };
}
